import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDuration } from "@/data/mockData";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Headphones, FileText, Clock, Search, Download, Bot, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import type { ContentStatus } from "@/types/content";
import { useQuery } from "@tanstack/react-query";
import { getContentListData, getProcessingStatuses } from "@/services/contentService";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import DownloadsList from "./DownloadsList";
import CategoriesTab from "./CategoriesTab";
import BulkImportTab from "./BulkImportTab";

const statusColors: Record<ContentStatus, string> = {
  published: "bg-primary/10 text-primary",
  draft: "bg-accent-gold/10 text-accent-gold",
  archived: "bg-muted text-muted-foreground",
};

export default function ContentList() {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { data, isLoading } = useQuery({
    queryKey: ["content-list-data"],
    queryFn: getContentListData,
  });

  // Load category names per content item from bridge table
  const { data: categoryMap = {} } = useQuery({
    queryKey: ["content-categories-map"],
    queryFn: async (): Promise<Record<string, string[]>> => {
      if (!isSupabaseConfigured || !supabase) return {};
      const [ccRes, catsRes] = await Promise.all([
        supabase.from("content_categories").select("content_item_id,category_id"),
        supabase.from("categories").select("id,name"),
      ]);
      if (!ccRes.data || !catsRes.data) return {};
      const nameById = new Map<string, string>();
      for (const cat of catsRes.data) nameById.set(cat.id, cat.name);
      const map: Record<string, string[]> = {};
      for (const row of ccRes.data) {
        const name = nameById.get(row.category_id);
        if (!name) continue;
        if (!map[row.content_item_id]) map[row.content_item_id] = [];
        map[row.content_item_id].push(name);
      }
      return map;
    },
  });

  const contentItems = data?.contentItems || [];
  const audioAssets = data?.audioAssets || [];
  const pdfAssets = data?.pdfAssets || [];

  const contentIds = contentItems.map((c) => c.id);
  const { data: processingMap = {} } = useQuery({
    queryKey: ["processing-statuses", contentIds.join(",")],
    queryFn: () => getProcessingStatuses(contentIds),
    enabled: contentIds.length > 0,
    refetchInterval: (query) => {
      const statuses = Object.values(query.state.data ?? {});
      return statuses.some((s) => s.status === "processing") ? 4000 : false;
    },
  });

  const filtered = contentItems.filter(c => {
    if (search && !c.title.toLowerCase().includes(search.toLowerCase()) && !c.author.toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter !== "all" && c.content_type !== typeFilter) return false;
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, statusFilter, pageSize]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-foreground">Contenido</h1>
        <Link to="/admin/content/new">
          <Button variant="default"><Plus className="h-4 w-4 mr-2" />Nuevo contenido</Button>
        </Link>
      </div>

      <Tabs defaultValue="catalog">
        <TabsList>
          <TabsTrigger value="catalog">Catálogo</TabsTrigger>
          <TabsTrigger value="categories">Categorías</TabsTrigger>
          <TabsTrigger value="import">Importar</TabsTrigger>
          <TabsTrigger value="downloads">Descargas</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="mt-4">
          <CategoriesTab />
        </TabsContent>

        <TabsContent value="import" className="mt-4">
          <BulkImportTab />
        </TabsContent>

        <TabsContent value="downloads" className="mt-4">
          <DownloadsList />
        </TabsContent>

        <TabsContent value="catalog" className="mt-4 space-y-4">

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar por título o autor..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="audio">Audio</SelectItem>
            <SelectItem value="pdf">Lectura</SelectItem>
              <SelectItem value="mixed">Mixto</SelectItem>
            </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="published">Publicado</SelectItem>
            <SelectItem value="draft">Borrador</SelectItem>
            <SelectItem value="archived">Archivado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">
          <p className="font-heading font-semibold mb-1">Cargando contenido...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="font-heading font-semibold mb-1">No se encontró contenido</p>
          <p className="text-sm">Intenta ajustar los filtros o crear nuevo contenido.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Autor</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Detalles</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead className="text-center">IA</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.map(item => {
                const audio = audioAssets.find(a => a.content_item_id === item.id);
                const pdf = pdfAssets.find(p => p.content_item_id === item.id);
                return (
                  <TableRow key={item.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {item.cover_image_url ? (
                          <img
                            src={item.cover_image_url}
                            alt={item.title}
                            className="h-12 w-10 rounded object-cover border border-border"
                          />
                        ) : (
                          <div className="h-12 w-10 rounded border border-dashed border-border bg-muted/40 flex items-center justify-center">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <Link to={`/admin/content/${item.id}`} className="font-medium text-foreground hover:text-primary">
                            {item.title}
                          </Link>
                          <p className="text-xs text-muted-foreground">{item.subtitle}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {(item.content_type === "audio" || item.content_type === "mixed") && (
                          <Badge variant="secondary" className="text-xs"><Headphones className="h-3 w-3 mr-1" />Audio</Badge>
                        )}
                        {(item.content_type === "pdf" || item.content_type === "mixed") && (
                          <Badge variant="secondary" className="text-xs"><FileText className="h-3 w-3 mr-1" />Lectura</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{item.author}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[item.status]}`}>
                        {item.status === "published" ? "Publicado" : item.status === "draft" ? "Borrador" : "Archivado"}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {audio && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDuration(audio.total_duration_seconds)}</span>}
                      {pdf && <span>{pdf.page_count} pág.</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(categoryMap[item.id] || (item.category ? [item.category] : [])).map((name) => (
                          <Badge key={name} variant="outline" className="text-xs">{name}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        const ps = processingMap[item.id];
                        if (!ps) return <span className="text-xs text-muted-foreground">—</span>;
                        if (ps.status === "completed") return (
                          <span title={`${ps.chunks_count} chunks`} className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                            <CheckCircle2 className="h-3 w-3" />{ps.chunks_count}
                          </span>
                        );
                        if (ps.status === "processing") return (
                          <span className="inline-flex items-center gap-1 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-full px-2 py-0.5">
                            <Loader2 className="h-3 w-3 animate-spin" />
                          </span>
                        );
                        if (ps.status === "failed") return (
                          <span title={ps.error_message ?? ""} className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                            <AlertCircle className="h-3 w-3" />
                          </span>
                        );
                        return <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">—</span>;
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-xs text-muted-foreground">
            {filtered.length === 0
              ? "0 elementos"
              : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, filtered.length)} de ${filtered.length}`}
          </p>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="h-8 w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 / página</SelectItem>
              <SelectItem value="20">20 / página</SelectItem>
              <SelectItem value="50">50 / página</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
          >
            Anterior
          </Button>
          <span className="text-xs text-muted-foreground">
            Página {currentPage} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            Siguiente
          </Button>
        </div>
      </div>

        </TabsContent>
      </Tabs>
    </div>
  );
}
